/**
 * AdminLeadsKanban.jsx — Meta Lead Ads pipeline board
 * 7 columns (new_lead → whatsapp_sent → documents_pending → documents_uploaded
 *           → doctor_assigned → report_ready → completed)
 * Drag lead cards between columns to update pipeline_stage on the backend.
 *
 * Backend: GET /api/admin/leads/kanban, PUT /api/admin/leads/{id}/stage
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { ArrowLeft, Phone, MessageCircle, Search, RefreshCw } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";

const STAGE_LABEL = {
  new_lead:            { title: "🆕 New Lead",         accent: "bg-blue-50 border-blue-200" },
  whatsapp_sent:       { title: "💬 WA Sent",           accent: "bg-teal-50 border-teal-200" },
  documents_pending:   { title: "⏳ Docs Pending",      accent: "bg-amber-50 border-amber-200" },
  documents_uploaded:  { title: "📄 Docs Received",     accent: "bg-lime-50 border-lime-200" },
  doctor_assigned:     { title: "👨‍⚕️ Doctor Assigned", accent: "bg-violet-50 border-violet-200" },
  report_ready:        { title: "📝 Report Ready",      accent: "bg-indigo-50 border-indigo-200" },
  completed:           { title: "✅ Completed",         accent: "bg-emerald-50 border-emerald-200" },
};

const SOURCE_ICON = {
  facebook_lead_ads: "📘",
  meta_ads: "📘",
  fab: "🌐",
  landing_wa: "🏠",
  "landing-hero": "🏠",
  callback: "📞",
  wa_widget: "💬",
};

function daysAgo(iso) {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
  if (d < 1) return "today";
  if (d < 2) return "1d";
  return `${Math.floor(d)}d`;
}

function LeadCard({ card, onNoteAdd }) {
  const src = card.source || "unknown";
  const icon = SOURCE_ICON[src] || "•";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow" data-testid={`kanban-card-${card.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900 text-sm truncate" title={card.name}>{card.name}</div>
          {card.phone && <div className="text-[11px] text-slate-500 font-mono truncate">{card.phone}</div>}
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 shrink-0" title={src}>
          {icon} {daysAgo(card.created_at)}
        </span>
      </div>
      {card.concern && <div className="mt-2 text-[11px] text-slate-600 line-clamp-2">{card.concern}</div>}
      {card.campaign && <div className="mt-1 text-[10px] text-slate-400 italic truncate" title={card.campaign}>{card.campaign}</div>}
      {card.notes && <div className="mt-1.5 text-[10px] bg-yellow-50 border border-yellow-100 rounded px-1.5 py-0.5 text-slate-700 line-clamp-2">📝 {card.notes}</div>}
      <div className="mt-2 flex items-center gap-1.5">
        {card.phone && (
          <a href={`tel:${card.phone}`} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 inline-flex items-center gap-0.5" data-testid={`kanban-call-${card.id}`}>
            <Phone className="w-2.5 h-2.5"/> Call
          </a>
        )}
        {card.phone && (
          <a href={`https://wa.me/${(card.phone || "").replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 inline-flex items-center gap-0.5" data-testid={`kanban-wa-${card.id}`}>
            <MessageCircle className="w-2.5 h-2.5"/> WA
          </a>
        )}
        <button onClick={() => onNoteAdd(card)} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100 ml-auto" data-testid={`kanban-note-${card.id}`}>
          + note
        </button>
      </div>
    </div>
  );
}

export default function AdminLeadsKanban() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // "all" | source

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/leads/kanban");
      setBoard(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Kanban");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filteredBoard = useMemo(() => {
    if (!board) return null;
    const q = search.trim().toLowerCase();
    const cols = {};
    for (const s of board.stages) {
      cols[s] = (board.columns[s] || []).filter(c => {
        if (filter !== "all" && (c.source || "") !== filter) return false;
        if (q) {
          const hay = `${c.name || ""} ${c.phone || ""} ${c.concern || ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });
    }
    return { ...board, columns: cols };
  }, [board, search, filter]);

  const sources = useMemo(() => {
    if (!board) return [];
    const s = new Set();
    for (const col of Object.values(board.columns)) col.forEach(c => c.source && s.add(c.source));
    return [...s];
  }, [board]);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId) return;

    // Optimistic UI update
    setBoard(prev => {
      const cols = { ...prev.columns };
      const from = [...cols[source.droppableId]];
      const [moved] = from.splice(source.index, 1);
      const to = [...cols[destination.droppableId]];
      to.splice(destination.index, 0, moved);
      cols[source.droppableId] = from;
      cols[destination.droppableId] = to;
      return { ...prev, columns: cols };
    });

    try {
      await api.put(`/admin/leads/${draggableId}/stage`, { stage: destination.droppableId });
      toast.success(`Moved to "${STAGE_LABEL[destination.droppableId].title.replace(/^\S+\s/, "")}"`);
    } catch (e) {
      toast.error("Failed to update stage — reverting");
      load(); // reload authoritative state
    }
  };

  const onNoteAdd = async (card) => {
    const note = window.prompt(`Add note for ${card.name}:`);
    if (!note) return;
    try {
      await api.post(`/admin/leads/${card.id}/note`, { note });
      toast.success("Note added");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add note");
    }
  };

  if (loading || !filteredBoard) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading Kanban…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-[1600px] mx-auto p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 text-sm" data-testid="kanban-back">
              <ArrowLeft className="w-4 h-4"/> Back to Admin
            </Link>
            <h1 className="font-heading text-2xl font-bold text-slate-900">📋 Lead Pipeline Board</h1>
            <span className="text-xs text-slate-500">({filteredBoard.total} total)</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <Input className="pl-9 h-9 w-56" placeholder="Search name / phone / concern" value={search} onChange={e => setSearch(e.target.value)} data-testid="kanban-search"/>
            </div>
            <select className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm" value={filter} onChange={e => setFilter(e.target.value)} data-testid="kanban-source-filter">
              <option value="all">All sources</option>
              {sources.map(s => <option key={s} value={s}>{SOURCE_ICON[s] || "•"} {s}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={load} data-testid="kanban-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1"/> Refresh
            </Button>
          </div>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${filteredBoard.stages.length}, minmax(260px, 1fr))`, overflowX: "auto" }}>
            {filteredBoard.stages.map(stage => {
              const label = STAGE_LABEL[stage] || { title: stage, accent: "bg-slate-50 border-slate-200" };
              const cards = filteredBoard.columns[stage] || [];
              return (
                <Droppable droppableId={stage} key={stage}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`rounded-xl border ${label.accent} ${snapshot.isDraggingOver ? "ring-2 ring-blue-400" : ""} p-3 min-h-[320px]`}
                      data-testid={`kanban-col-${stage}`}
                    >
                      <div className="flex items-center justify-between mb-3 sticky top-0">
                        <div className="font-heading text-sm font-semibold text-slate-900">{label.title}</div>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-white/70 text-slate-700 border border-slate-200">{cards.length}</span>
                      </div>
                      <div className="space-y-2">
                        {cards.map((c, i) => (
                          <Draggable draggableId={c.id} index={i} key={c.id}>
                            {(prov) => (
                              <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}>
                                <LeadCard card={c} onNoteAdd={onNoteAdd}/>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {cards.length === 0 && (
                          <div className="text-xs text-slate-400 italic py-4 text-center border border-dashed border-slate-200 rounded-lg">
                            Drop leads here
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>
      </div>
    </div>
  );
}
